import { useEffect, useRef, useState } from "react";
import { IconDownload, IconPlayerPause, IconPlayerPlay, IconTrash } from "@tabler/icons-react";
import WaveSurfer from "wavesurfer.js";
import { formatAudioTime } from "@utils/media/audioTime";

export default function AiAudioWavePreview({
  url = "",
  onDownload,
  isDownloading = false,
  isGenerating = false,
  showDownload = true,
  onRemove,
}) {
  const hostRef = useRef(null);
  const waveRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!hostRef.current || !url) return undefined;
    setCurrentTime(0);
    setDuration(0);
    const wave = WaveSurfer.create({
      container: hostRef.current,
      url,
      fetchParams: { credentials: "include" },
      height: 32,
      barWidth: 2,
      barGap: 1,
      cursorWidth: 1,
      waveColor: "#94a3b8",
      progressColor: "#0ea5e9",
      cursorColor: "#f8fafc",
      normalize: true,
    });
    waveRef.current = wave;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onReady = (audioDuration) => setDuration(audioDuration || 0);
    const onTimeUpdate = (time) => setCurrentTime(time || 0);
    const onFinish = () => {
      setIsPlaying(false);
      setCurrentTime(wave.getDuration() || 0);
    };
    wave.on("play", onPlay);
    wave.on("pause", onPause);
    wave.on("ready", onReady);
    wave.on("timeupdate", onTimeUpdate);
    wave.on("finish", onFinish);
    return () => {
      wave.un("play", onPlay);
      wave.un("pause", onPause);
      wave.un("ready", onReady);
      wave.un("timeupdate", onTimeUpdate);
      wave.un("finish", onFinish);
      wave.destroy();
      waveRef.current = null;
    };
  }, [url]);

  useEffect(() => {
    if (isGenerating && isPlaying) waveRef.current?.pause();
  }, [isGenerating, isPlaying]);

  return (
    <div className="mode-route-description-ai-wave-preview">
      <button
        type="button"
        className="mode-route-description-ai-wave-action"
        onClick={(event) => {
          event.stopPropagation();
          waveRef.current?.playPause();
        }}
        disabled={isGenerating}
        aria-label={isPlaying ? "Pause generated audio" : "Play generated audio"}
        title={isGenerating ? "Generating audio" : isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />}
      </button>
      <div
        ref={hostRef}
        className="mode-route-description-ai-wave-host"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      />
      <span className="mode-route-description-ai-wave-time">
        {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
      </span>
      {typeof onRemove === "function" ? (
        <button
          type="button"
          className="mode-route-description-ai-wave-action is-delete"
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
          disabled={isGenerating}
          aria-label="Remove generated music"
          title="Remove generated music"
        >
          <IconTrash size={16} />
        </button>
      ) : showDownload ? (
        <button
          type="button"
          className="mode-route-description-ai-wave-action"
          onClick={onDownload}
          disabled={isDownloading || isGenerating}
          aria-label="Download generated audio"
          title={isGenerating ? "Generating audio" : isDownloading ? "Downloading audio" : "Download audio"}
        >
          <IconDownload size={16} />
        </button>
      ) : null}
    </div>
  );
}
